import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  Service,
  ServiceData,
  ServiceListParams,
  ServiceVariant,
} from '@/libs/interfaces'
import { servicesApiMock } from '@/libs/mocks/handlers/services'

const servicesApiReal = {
  create: async (body: Partial<Service>): Promise<CreateApiResult<Partial<Service>>> => {
    return await api
      .post<ApiResponse<Partial<Service>>>(URLs.Services.create(body.orgID || '', 'Local'), body)
      .then((response) => ({
        data: response.data.data,
        status: response.status,
        message: response.message,
      }))
  },
  getByID: async (params: { orgID?: string; serviceID?: string }): Promise<Partial<Service>> => {
    const { orgID, serviceID } = params
    return await api
      .get<ApiResponse<Partial<Service>>>(URLs.Services.byID(orgID ?? '', 'Local', serviceID ?? ''))
      .then((response) => response.data.data)
  },
  list: async (params: ServiceListParams): Promise<Service[]> => {
    const { orgID, storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<ServiceData>>(URLs.Services.list(orgID, storeID, query))
      .then((response) => response?.data?.data?.services || [])
  },
  listCursor: async (params: ServiceListParams): Promise<CursorPaginatedResponse<Service>> => {
    const { orgID, storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<ServiceData>>(URLs.Services.list(orgID, storeID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.services,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  update: async (payload: Partial<Service>) => {
    const { orgID, serviceID, ...patchData } = payload
    return await api
      .patch(URLs.Services.byID(orgID || '', 'Local', serviceID || ''), patchData)
      .then((response) => response.data)
  },
  delete: async (payload: Partial<Service>): Promise<Partial<Service>> => {
    const { orgID, serviceID } = payload
    return await api
      .delete<ApiResponse<Partial<Service>>>(
        URLs.Services.byID(orgID || '', 'Local', serviceID || ''),
      )
      .then((response) => response.data?.data)
  },
  createVariant: async (body: Partial<ServiceVariant>): Promise<Partial<ServiceVariant>> => {
    return await api
      .post<ApiResponse<Partial<ServiceVariant>>>(
        URLs.Services.createVariant(body.orgID || '', body?.storeID || '', body?.serviceID || ''),
        body,
      )
      .then((response) => response.data.data)
  },
  deleteVariant: async (payload: Partial<ServiceVariant>): Promise<Partial<ServiceVariant>> => {
    const { orgID, serviceID, storeID, serviceVariantID } = payload
    return await api
      .delete<ApiResponse<Partial<ServiceVariant>>>(
        URLs.Services.byIDVariant(
          orgID || '',
          storeID || '',
          serviceID || '',
          serviceVariantID || '',
        ),
      )
      .then((response) => response.data?.data)
  },
  updateVariant: async (payload: Partial<ServiceVariant>): Promise<Partial<ServiceVariant>> => {
    const { orgID, serviceID, storeID, ...patchData } = payload
    return await api
      .patch<ApiResponse<Partial<ServiceVariant>>>(
        URLs.Services.byIDVariant(
          orgID || '',
          storeID || '',
          serviceID || '',
          patchData?.serviceVariantID || '',
        ),
        patchData,
      )
      .then((response) => response.data?.data)
  },
}

export const servicesApi: typeof servicesApiReal = __IS_MOCK_MODE__
  ? servicesApiMock
  : servicesApiReal
